"use client"

import { useState } from "react"
import Image from "next/image"
import { X } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/lib/auth-store"
import { markSeen } from "@/lib/client/seen-state"
import { useTranslations } from "next-intl"
import { useModalContent } from "@/lib/modal-content"
import { GLYMTOBER_DAILY_GOONCOIN, STANDARD_DAILY_GOONCOIN } from "@/lib/glymtober"

export function glymtoberSeenKeyForYear(year: number) { return `modal:glymtober_${year}` }
export function localGlymtoberSeenKeyForYear(year: number) { return `glymtober_${year}_dismissed` }

/** Presentational content for the Glymtober welcome, extracted from what
 * used to be the standalone GlymtoberModal so day-one-promo-stack.tsx can
 * own eligibility/sequencing (alongside Early Access) while this only ever
 * renders once the stack has already decided to show it. */
export function GlymtoberCard({ onDone, onReady }: { onDone: () => void; onReady?: () => void }) {
  const t = useTranslations("glymtoberModal")
  const content = useModalContent("glymtober_welcome", true)
  const [heroLoaded, setHeroLoaded] = useState(true)
  const { user } = useAuthStore()

  const dismiss = () => {
    const year = new Date().getFullYear()
    if (user) {
      void markSeen(glymtoberSeenKeyForYear(year))
    } else {
      try { localStorage.setItem(localGlymtoberSeenKeyForYear(year), "1") } catch {}
    }
    onDone()
  }

  if (!heroLoaded || !content.ready) return null

  const boostTimes = Math.round(GLYMTOBER_DAILY_GOONCOIN / STANDARD_DAILY_GOONCOIN)

  return (
    // goopborder-slime-portrait.webp (941×1672, ratio 0.5628 ≈ 9:16) — sized to
    // its own native ratio so the frame is never stretched off-proportion.
    // No max-w-sm here — the shared shell (day-one-promo-stack.tsx /
    // glymtober-modal.tsx's standalone preview wrapper) already constrains
    // width; this only needs to fill it.
    <div className="relative w-full aspect-[9/16]">
      {/* Clipped content layer — art + text/button. Rounded to match the
          border's own corner radius so nothing square peeks out past it. */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden bg-background shadow-2xl">
        {/* Plain <img>: an uploaded override lives on the storage host, which
            next/image would need whitelisting for. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={content.art("hero")}
          alt={t("welcomeToGlymtober")}
          className="absolute inset-0 w-full h-full object-cover"
          ref={(el) => { if (el && el.complete && el.naturalWidth > 0) onReady?.() }}
          onLoad={() => onReady?.()}
          onError={() => { setHeroLoaded(false); onReady?.() }}
        />

        {/* Spinning Gooncoin accent, roughly under the claw — purely
            decorative, same asset gooncoin-added-popup.tsx uses at a much
            bigger size. brightness(1.15) + a soft lavender glow (on top of
            the grounding drop-shadow) so it reads as lit up, not just
            pasted on. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/gooncoin/gooncoin_spin.gif"
          alt=""
          className="absolute w-20 h-20 pointer-events-none [filter:brightness(1.15)_drop-shadow(0_4px_10px_rgba(0,0,0,0.5))_drop-shadow(0_0_18px_rgba(196,165,255,0.85))]"
          style={{ left: "39%", top: "52%" }}
        />

        {/* Shadow rising up behind the text/button — explicit gradient
            (same "fades up behind text" pattern as energy-limit-modal.tsx),
            not just relying on the art's own natural darkness at the
            bottom. Starts higher (top: 48%) than the text block itself so
            there's real contrast behind it, not just right at its edge. */}
        <div
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            top: "48%",
            background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.9) 62%, rgba(0,0,0,0.95) 100%)",
          }}
        />

        {/* Title/body/button float over the art's own dark lower zone, sized
            naturally from the bottom (same as Founder's own overlay — no
            fixed top). A fixed top-N% window was tried and rejected: this
            block is taller than the art's actual dark band, so pinning both
            edges pushed the button below the image entirely, past this
            layer's own overflow-hidden boundary — invisible. */}
        <div className="absolute inset-x-0 bottom-0 px-7 pb-6 pt-3 flex flex-col gap-6">
          <div className="space-y-2 text-center">
            <p className="text-2xl font-bold text-orange-400 [text-shadow:_0_2px_10px_rgb(0_0_0_/_90%),_0_1px_3px_rgb(0_0_0_/_100%)]">
              {content.text("title", t("welcomeToGlymtober"))}
            </p>
            <p className="text-base text-white/90 leading-relaxed [text-shadow:_0_2px_8px_rgb(0_0_0_/_85%),_0_1px_2px_rgb(0_0_0_/_100%)]">
              {content.text("body_pre", t("allMonthLongYourDailyLoginBonusIsBoostedT"))}{" "}
              <span className="font-bold text-orange-400">
                {content.text("body_stat", t("amountGooncoinADay", { amount: GLYMTOBER_DAILY_GOONCOIN }), { amount: String(GLYMTOBER_DAILY_GOONCOIN) })}
              </span>{" "}
              {content.text("body_post", t("timesTheUsualAmount", { times: boostTimes }), { times: String(boostTimes) })}
            </p>
          </div>

          <Button
            onClick={dismiss}
            className="w-full h-12 rounded-full text-base bg-orange-500 hover:bg-orange-400 text-white border-0 shadow-[0_0_16px_rgba(249,115,22,0.4)]"
          >
            {content.text("button", t("letSGo"))}
          </Button>
        </div>
      </div>

      {/* More visible than the other modals' idle opacity-50 — this card's
          art is busier/darker overall, so the dismiss control needs to
          read at a glance without adding a competing color. */}
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 z-30 h-8 w-8 rounded-full bg-black/70 backdrop-blur-md border border-white/25 flex items-center justify-center text-white opacity-90 hover:opacity-100 hover:bg-black/85 active:opacity-100 focus-visible:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Goop border — unclipped, overscanning slightly past the card's own
          edges (same -3%/-3% convention as app/too-young/page.tsx's use of
          this same asset) so the frame reads as wrapping AROUND the card
          rather than sitting inset inside it. */}
      <div className="absolute z-20 pointer-events-none" style={{ inset: "-3% -3%" }}>
        <Image src="/borders/goopborder-slime-portrait.webp" alt="" fill style={{ objectFit: "fill" }} unoptimized />
      </div>
    </div>
  )
}
